import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import { profileImageService } from './profileImageService';

const log = createComponentLogger('ProfileImageServiceV2', 'storage');

interface UploadResult {
  url: string;
  path: string;
}

interface CropData {
  x: number;
  y: number;
  width: number;
  height: number;
  scale: number;
}

interface DualPhotoUploadResult extends UploadResult {
  photoType: 'facial' | 'body';
  thumbnailUrl?: string;
}

/**
 * How photo changes are persisted to the users table.
 * - 'direct': plain users UPDATE (self/admin — covered by users RLS).
 * - 'partner_rpc': through the audited partner_update_managed_user RPC,
 *   which is the only write path partners are authorized for.
 */
export type PhotoPersistMode = 'direct' | 'partner_rpc';

interface PhotoPersistOptions {
  persistVia?: PhotoPersistMode;
}

/**
 * V2 Profile Image Service with dual photo support
 * Maintains full backwards compatibility with V1 while adding new functionality
 */
class ProfileImageServiceV2 {
  private readonly MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB for dual photos
  private readonly ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  private readonly DIMENSIONS = {
    // Legacy dimensions (maintained for compatibility)
    profile: { width: 400, height: 400 },
    signature: { width: 600, height: 200 },
    thumbnail: { width: 100, height: 100 },
    // New dual photo dimensions
    facial: { width: 600, height: 600 },
    facialThumb: { width: 150, height: 150 },
    body: { width: 600, height: 900 },
    bodyThumb: { width: 150, height: 225 }
  };

  private v1Service = profileImageService;

  /**
   * Check if dual photo feature is enabled for user
   */
  async isDualPhotoEnabled(userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .rpc('check_dual_photo_access', { p_user_id: userId });
      
      if (error) {
        log.error('Failed to check dual photo access', { userId }, error);
        return false;
      }
      
      return data === true;
    } catch (error) {
      log.error('Error checking dual photo access', { userId }, error as Error);
      return false;
    }
  }

  /**
   * Backwards compatible upload method
   * Routes to appropriate handler based on type and feature flag
   */
  async uploadProfileImage(
    userId: string,
    file: File,
    type: 'profile' | 'signature' = 'profile'
  ): Promise<UploadResult> {
    // Check if dual photo is enabled
    const _dualPhotoEnabled = await this.isDualPhotoEnabled(userId);
    
    if (type === 'signature') {
      // Signature uploads remain unchanged
      return this.v1Service.uploadProfileImage(userId, file, type);
    }
    
    if (type === 'profile') {
      if (_dualPhotoEnabled) {
        // For dual photo users, 'profile' uploads go to facial
        log.info('Routing profile upload to facial photo', { userId });
        return this.uploadDualPhoto(userId, file, 'facial');
      } else {
        // For non-dual photo users, use legacy upload
        return this.v1Service.uploadProfileImage(userId, file, type);
      }
    }
    
    // Default to V1 for any unrecognized types
    return this.v1Service.uploadProfileImage(userId, file, type);
  }

  /**
   * New method for uploading dual photos
   */
  async uploadDualPhoto(
    userId: string,
    file: File,
    photoType: 'facial' | 'body',
    cropData?: CropData,
    options?: PhotoPersistOptions
  ): Promise<DualPhotoUploadResult> {
    try {
      // Validate input
      this.validateFile(file);
      
      // Get dimensions for this photo type
      const dimensions = this.DIMENSIONS[photoType];
      const thumbDimensions = photoType === 'facial' 
        ? this.DIMENSIONS.facialThumb 
        : this.DIMENSIONS.bodyThumb;
      
      // Apply crop if provided
      let processedFile = file;
      if (cropData) {
        processedFile = await this.applyCrop(file, cropData);
      }
      
      // Optimize the main image
      const optimizedBlob = await this.optimizeImage(
        processedFile,
        dimensions.width,
        dimensions.height,
        0.85
      );
      
      // Create thumbnail
      const thumbnailBlob = await this.optimizeImage(
        processedFile,
        thumbDimensions.width,
        thumbDimensions.height,
        0.80
      );
      
      // Generate filenames with timestamp for cache busting
      const timestamp = Date.now();
      const mainFileName = `${userId}/${photoType}_${timestamp}.jpg`;
      const thumbFileName = `${userId}/${photoType}_thumb_${timestamp}.jpg`;
      
      log.info('Uploading dual photo', { 
        userId, 
        photoType, 
        mainFile: mainFileName,
        thumbFile: thumbFileName 
      });
      
      // Upload main image
      const { data: mainData, error: mainError } = await supabase.storage
        .from('profile-images')
        .upload(mainFileName, optimizedBlob, {
          contentType: 'image/jpeg',
          cacheControl: '3600',
          upsert: false // Don't overwrite, create new
        });
      
      if (mainError) {
        throw new Error(`Failed to upload main image: ${mainError.message}`);
      }
      
      // Upload thumbnail
      const { data: thumbData, error: thumbError } = await supabase.storage
        .from('profile-images')
        .upload(thumbFileName, thumbnailBlob, {
          contentType: 'image/jpeg',
          cacheControl: '3600',
          upsert: false
        });
      
      if (thumbError) {
        // Clean up main image if thumbnail fails
        await supabase.storage
          .from('profile-images')
          .remove([mainFileName]);
        throw new Error(`Failed to upload thumbnail: ${thumbError.message}`);
      }
      
      // Get public URLs
      const { data: { publicUrl: mainUrl } } = supabase.storage
        .from('profile-images')
        .getPublicUrl(mainData.path);
      
      const { data: { publicUrl: thumbUrl } } = supabase.storage
        .from('profile-images')
        .getPublicUrl(thumbData.path);
      
      // Update database with new URLs
      const persistVia = options?.persistVia ?? 'direct';
      try {
        if (persistVia === 'partner_rpc') {
          await this.persistDualPhotoViaPartnerRpc(userId, photoType, mainUrl, cropData);
        } else {
          await this.updateDualPhotoInDatabase(
            userId,
            photoType,
            mainUrl,
            thumbUrl,
            cropData
          );
        }
      } catch (persistError) {
        if (persistVia === 'partner_rpc') {
          // Don't orphan the just-uploaded files when the audited RPC
          // persist fails — best-effort cleanup, then surface the error.
          try {
            await supabase.storage
              .from('profile-images')
              .remove([mainFileName, thumbFileName]);
          } catch (cleanupError) {
            log.error('Failed to clean up orphaned photo files', { userId, photoType }, cleanupError as Error);
          }
        }
        throw persistError;
      }

      // Clean up old files
      await this.cleanupOldPhotos(userId, photoType, timestamp);

      // Log migration if this is the first dual photo upload. Skipped for
      // partner persistence: the migration log insert is not part of the
      // partner write surface and would only fail against RLS.
      if (persistVia !== 'partner_rpc') {
        await this.logMigrationIfNeeded(userId, photoType);
      }
      
      return {
        url: mainUrl,
        path: mainData.path,
        photoType,
        thumbnailUrl: thumbUrl
      };
    } catch (error) {
      log.error('Error in uploadDualPhoto', { userId, photoType }, error as Error);
      throw error;
    }
  }

  /**
   * Get profile image URL with backwards compatibility
   */
  async getProfileImageUrl(
    userId: string,
    type: 'profile' | 'signature' | 'facial' | 'body' = 'profile'
  ): Promise<string | null> {
    try {
      // For signature, always use V1
      if (type === 'signature') {
        return this.v1Service.getProfileImageUrl(userId, type);
      }
      
      const _dualPhotoEnabled = await this.isDualPhotoEnabled(userId);
      
      if (type === 'profile') {
        // For 'profile' requests, return best available photo
        const { data, error } = await supabase
          .from('users')
          .select('profile_photo_facial, profile_photo_body')
          .eq('id', userId)
          .single();
        
        if (!error && data) {
          // Prefer facial photo if available, otherwise body (NO LEGACY)
          return data.profile_photo_facial || data.profile_photo_body || null;
        }
      }
      
      if (type === 'facial' || type === 'body') {
        // Direct request for dual photo
        const column = type === 'facial' ? 'profile_photo_facial' : 'profile_photo_body';
        const { data, error } = await supabase
          .from('users')
          .select(column)
          .eq('id', userId)
          .single();
        
        if (!error && data && data[column]) {
          return data[column];
        }
      }
      
      return null;
    } catch (error) {
      log.error('Error getting profile image URL', { userId, type }, error as Error);
      return null;
    }
  }

  /**
   * Get both facial and body photos for a user
   */
  async getDualPhotos(userId: string): Promise<{
    facial: string | null;
    body: string | null;
    hasDualPhotos: boolean;
  }> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('profile_photo_facial, profile_photo_body')
        .eq('id', userId)
        .single();
      
      if (error || !data) {
        return { facial: null, body: null, hasDualPhotos: false };
      }
      
      return {
        facial: data.profile_photo_facial,
        body: data.profile_photo_body,
        hasDualPhotos: !!(data.profile_photo_facial && data.profile_photo_body)
      };
    } catch (error) {
      log.error('Error getting dual photos', { userId }, error as Error);
      return { facial: null, body: null, hasDualPhotos: false };
    }
  }

  /**
   * Delete profile image with cleanup
   */
  async deleteProfileImage(
    userId: string,
    type: 'profile' | 'signature' | 'facial' | 'body' = 'profile',
    options?: PhotoPersistOptions
  ): Promise<void> {
    try {
      if (type === 'signature') {
        return this.v1Service.deleteProfileImage(userId, type);
      }

      const _dualPhotoEnabled = await this.isDualPhotoEnabled(userId);

      if (!_dualPhotoEnabled && type === 'profile') {
        return this.v1Service.deleteProfileImage(userId, type);
      }

      // Handle dual photo deletion
      const column = type === 'facial' ? 'profile_photo_facial' :
                    type === 'body' ? 'profile_photo_body' :
                    'profile_photo';

      // Get current URL to extract filename
      const { data: profile } = await supabase
        .from('users')
        .select(column)
        .eq('id', userId)
        .single();

      const persistVia = options?.persistVia ?? 'direct';

      if (persistVia === 'partner_rpc') {
        // Clear the DB FIRST through the audited RPC, then best-effort
        // storage cleanup — never leave a profile pointing at a deleted
        // object if the second step fails.
        const { error } = await supabase.rpc('partner_update_managed_user', {
          target_user_id: userId,
          updates: { [column]: null }
        });

        if (error) {
          throw new Error(`Failed to update database: ${error.message}`);
        }

        if (profile && profile[column]) {
          try {
            await this.removeDualPhotoFiles(userId, type, profile[column]);
          } catch (cleanupError) {
            log.error('Best-effort photo file cleanup failed', { userId, type }, cleanupError as Error);
          }
        }

        log.info('Photo deleted', { userId, type, persistVia });
        return;
      }

      if (profile && profile[column]) {
        await this.removeDualPhotoFiles(userId, type, profile[column]);
      }

      // Clear from database
      const { error } = await supabase
        .from('users')
        .update({ [column]: null })
        .eq('id', userId);

      if (error) {
        throw error;
      }

      log.info('Photo deleted', { userId, type });
    } catch (error) {
      log.error('Error deleting photo', { userId, type }, error as Error);
      throw error;
    }
  }

  /**
   * Remove a dual photo's main + thumbnail objects from storage based on the
   * stored public URL.
   */
  private async removeDualPhotoFiles(
    userId: string,
    type: 'profile' | 'facial' | 'body',
    photoUrl: string
  ): Promise<void> {
    // Extract filename from URL
    const url = new URL(photoUrl);
    const pathParts = url.pathname.split('/');
    const filename = pathParts[pathParts.length - 1];

    // Delete from storage
    await supabase.storage
      .from('profile-images')
      .remove([`${userId}/${filename}`]);

    // Delete thumbnail if it exists
    const thumbFilename = filename.replace(
      `${type}_`,
      `${type}_thumb_`
    );
    await supabase.storage
      .from('profile-images')
      .remove([`${userId}/${thumbFilename}`]);
  }

  // Private helper methods

  private validateFile(file: File): void {
    if (!file) {
      throw new Error('No file provided');
    }
    
    if (!this.ALLOWED_TYPES.includes(file.type)) {
      throw new Error(`Invalid file type. Allowed types: ${this.ALLOWED_TYPES.join(', ')}`);
    }
    
    if (file.size > this.MAX_FILE_SIZE) {
      throw new Error(`File too large. Maximum size: ${this.MAX_FILE_SIZE / 1024 / 1024}MB`);
    }
  }

  private async optimizeImage(
    file: File | Blob,
    maxWidth: number,
    maxHeight: number,
    quality: number = 0.85
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      const imageUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(imageUrl);

        let { width, height } = img;

        // Scale to fit within target dimensions (both up and down)
        // This ensures small crops get scaled up to target size
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);

        canvas.width = width;
        canvas.height = height;

        // Enable image smoothing for better quality when scaling up
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          'image/jpeg',
          quality
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(imageUrl);
        reject(new Error('Failed to load image'));
      };

      img.src = imageUrl;
    });
  }

  private async applyCrop(file: File, cropData: CropData): Promise<File> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }
      
      const imageUrl = URL.createObjectURL(file);
      
      img.onload = () => {
        URL.revokeObjectURL(imageUrl);
        
        canvas.width = cropData.width;
        canvas.height = cropData.height;
        
        ctx.drawImage(
          img,
          cropData.x,
          cropData.y,
          cropData.width,
          cropData.height,
          0,
          0,
          cropData.width,
          cropData.height
        );
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(new File([blob], file.name, { type: 'image/jpeg' }));
            } else {
              reject(new Error('Failed to crop image'));
            }
          },
          'image/jpeg',
          0.95
        );
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(imageUrl);
        reject(new Error('Failed to load image for cropping'));
      };
      
      img.src = imageUrl;
    });
  }

  /**
   * Persist a dual-photo change for a PARTNER-managed user through the
   * audited partner_update_managed_user RPC (partners have no direct users
   * UPDATE access). Sends only the current photo type's crop patch — the RPC
   * merges it into photo_crop_data so the other type's crop is preserved.
   */
  private async persistDualPhotoViaPartnerRpc(
    userId: string,
    photoType: 'facial' | 'body',
    mainUrl: string,
    cropData?: CropData
  ): Promise<void> {
    const column = photoType === 'facial' ? 'profile_photo_facial' : 'profile_photo_body';

    const updates: Record<string, unknown> = {
      [column]: mainUrl
    };

    if (cropData) {
      updates.photo_crop_data = { [photoType]: cropData };
    }

    const { error } = await supabase.rpc('partner_update_managed_user', {
      target_user_id: userId,
      updates
    });

    if (error) {
      throw new Error(`Failed to update database: ${error.message}`);
    }
  }

  private async updateDualPhotoInDatabase(
    userId: string,
    photoType: 'facial' | 'body',
    mainUrl: string,
    thumbUrl: string,
    cropData?: CropData
  ): Promise<void> {
    const column = photoType === 'facial' ? 'profile_photo_facial' : 'profile_photo_body';
    
    // Prepare update data
    const updateData: Record<string, unknown> = {
      [column]: mainUrl
    };
    
    // DEPRECATED: No longer updating legacy profile_photo field
    // Legacy field will be removed in future migration
    // if (photoType === 'facial') {
    //   updateData.profile_photo = mainUrl;
    // }
    
    // Store crop data if provided
    if (cropData) {
      const { data: currentCropData } = await supabase
        .from('users')
        .select('photo_crop_data')
        .eq('id', userId)
        .maybeSingle();
      
      const existingCropData = currentCropData?.photo_crop_data || {};
      updateData.photo_crop_data = {
        ...existingCropData,
        [photoType]: cropData
      };
    }
    
    const { error } = await supabase
      .from('users')
      .update(updateData)
      .eq('id', userId);
    
    if (error) {
      throw new Error(`Failed to update database: ${error.message}`);
    }
  }

  private async cleanupOldPhotos(
    userId: string,
    photoType: 'facial' | 'body',
    currentTimestamp: number
  ): Promise<void> {
    try {
      const { data: files } = await supabase.storage
        .from('profile-images')
        .list(userId, { limit: 100 });
      
      if (!files) {
return;
}
      
      // Find old photos of the same type
      const oldPhotos = files.filter(file => {
        const isCorrectType = file.name.startsWith(`${photoType}_`);
        const isNotCurrent = !file.name.includes(currentTimestamp.toString());
        return isCorrectType && isNotCurrent;
      });
      
      if (oldPhotos.length > 0) {
        const filesToDelete = oldPhotos.map(f => `${userId}/${f.name}`);
        await supabase.storage
          .from('profile-images')
          .remove(filesToDelete);
        
        log.info('Cleaned up old photos', { 
          userId, 
          photoType, 
          deletedCount: filesToDelete.length 
        });
      }
    } catch (error) {
      log.error('Error cleaning up old photos', { userId, photoType }, error as Error);
      // Don't throw - cleanup failure shouldn't break upload
    }
  }

  private async logMigrationIfNeeded(userId: string, photoType: 'facial' | 'body'): Promise<void> {
    try {
      // Only log facial photo uploads to migration log
      if (photoType === 'facial') {
        await supabase
          .from('photo_migration_log')
          .insert({
            user_id: userId,
            migration_type: 'manual',
            status: 'success',
            completed_at: new Date().toISOString(),
            metadata: { photo_type: photoType }
          });
      }
    } catch (error) {
      log.error('Error logging migration', { userId, photoType }, error as Error);
      // Don't throw - logging failure shouldn't break upload
    }
  }
}

// Export singleton instance
export const profileImageServiceV2 = new ProfileImageServiceV2();

// Also export the class for testing
export { ProfileImageServiceV2 };